import _ from 'lodash';
import { browserHistory } from 'react-router';
import toastr from 'toastr';
/** import third-party libraries in the section above, keep the ascending order */

import { graphqlRequest } from 'utils/http/redux';
import BusinessReportActions from 'states/resources/business-reports/actions';
import querystring from 'utils/querystring';
/** import classes and others in the section above, keep the ascending order */

const TOASTR_OPTIONS = {
    tapToDismiss: true,
    showDuration: 1000,
    showMethod: 'slideDown',
    timeOut: 0,
    extendedTimeOut: 0,
    positionClass: 'toast-bottom-left',
};

const DEFAULT_ITEMS_PER_PAGE = 25;

/**
 * Builds GraphQL BusinessReportsFilters with required pagination (page, limit).
 *
 * @param {Object} rawFilters - URL/dashboard filters (page, search, type, …)
 * @param {number} itemsPerPage - Page size from dashboard state
 * @returns {Object} Variables for businessReports(filters: …)
 */
function buildBusinessReportsFilters(rawFilters = {}, itemsPerPage = DEFAULT_ITEMS_PER_PAGE) {
    const filters = {
        page: parseInt(rawFilters.page || 1, 10),
        limit: parseInt(itemsPerPage, 10),
    };

    if (rawFilters.search) {
        filters.search = rawFilters.search;
    }

    if (rawFilters.type) {
        filters.type = rawFilters.type;
    }

    return filters;
}

const actions = {
    init(filters) {
        return dispatch => {
            dispatch({
                type: 'BUSINESS_REPORT_DASHBOARD__INIT__START',
                payload: {
                    filters,
                },
            });

            return dispatch(actions.fetchPageData(filters)).then(
                () => {
                    dispatch({
                        type: 'BUSINESS_REPORT_DASHBOARD__INIT__END',
                        payload: {},
                    });
                },
                error => {
                    dispatch({
                        type: 'BUSINESS_REPORT_DASHBOARD__INIT__ERROR',
                        payload: {},
                        error,
                    });

                    throw error;
                }
            );
        };
    },
    delete(businessReportId, filters) {
        return async dispatch => {
            try {
                dispatch({
                    type: 'BUSINESS_REPORT_DASHBOARD__DELETE__START',
                    payload: {
                        businessReportId,
                    },
                });

                const businessReport = await dispatch(
                    BusinessReportActions.delete(businessReportId)
                );

                if (businessReport) {
                    dispatch({
                        type: 'BUSINESS_REPORT_DASHBOARD__DELETE__SUCCESS',
                        payload: {
                            businessReport,
                        },
                    });
                } else {
                    toastr.error(
                        '',
                        `<p>There was an error deleting the business report.</p>`,
                        TOASTR_OPTIONS
                    );
                    dispatch({
                        type: 'BUSINESS_REPORT_DASHBOARD__DELETE__FAILED',
                        payload: {},
                    });
                }
            } catch (error) {
                toastr.error(
                    '',
                    `<p>There was an error deleting the business report.</p>`,
                    TOASTR_OPTIONS
                );
                dispatch({
                    type: 'BUSINESS_REPORT_DASHBOARD__DELETE__ERROR',
                    payload: {},
                    error,
                });
            }

            try {
                await dispatch(actions.fetchPageData(filters));
            } catch (error) {
                dispatch({
                    type: 'BUSINESS_REPORT_DASHBOARD__FETCH_PAGE_DATA__ERROR',
                    payload: {},
                    error,
                });
            }
        };
    },
    fetchPageData(filters) {
        return async (dispatch, getState) => {
            const itemsPerPage = _.get(
                getState(),
                'businessReportDashboard.itemsPerPage',
                DEFAULT_ITEMS_PER_PAGE
            );
            const graphqlFilters = buildBusinessReportsFilters(filters, itemsPerPage);

            dispatch({
                type: 'BUSINESS_REPORT_DASHBOARD__FETCH_PAGE_DATA__START',
                payload: {},
            });

            try {
                const {
                    businessReports,
                    maxResults,
                    allBusinessReportCount,
                    myBusinessReportCount,
                } = await dispatch(actions.getBusinessReports(graphqlFilters));

                return dispatch({
                    type: 'BUSINESS_REPORT_DASHBOARD__FETCH_PAGE_DATA__END',
                    payload: {
                        businessReports,
                        maxResults,
                        page: graphqlFilters.page,
                        allBusinessReportCount,
                        myBusinessReportCount,
                    },
                });
            } catch (error) {
                dispatch({
                    type: 'BUSINESS_REPORT_DASHBOARD__FETCH_PAGE_DATA__START',
                    payload: {},
                    error,
                });

                throw error;
            }
        };
    },
    search(key, value, filters) {
        return () => {
            const query = {
                ...filters,
                [key]: value,
                page: 1,
            };
            browserHistory.push(`/reports?${querystring.serialize(query)}`);
        };
    },
    openDeleteConfirmationModal(businessReportId) {
        return dispatch => {
            dispatch({
                type: 'BUSINESS_REPORT_DASHBOARD__OPEN_DELETE_CONFIRMATION_MODAL',
                payload: {
                    businessReportId,
                },
            });
        };
    },
    closeDeleteConfirmationModal() {
        return dispatch => {
            dispatch({
                type: 'BUSINESS_REPORT_DASHBOARD__CLOSE_DELETE_CONFIRMATION_MODAL',
                payload: {},
            });
        };
    },
    reset() {
        return dispatch => {
            dispatch(actions.init({}));
            dispatch(actions.search('', {}));
        };
    },
    getBusinessReports(filters) {
        return async dispatch => {
            try {
                const {
                    data: { businessReports, allBusinessReportCount, myBusinessReportCount },
                } = await dispatch(
                    graphqlRequest({
                        query: `
                        query getBusinessReports($filters: BusinessReportsFilters!) {
                            myBusinessReportCount
                            allBusinessReportCount
                            businessReports(filters: $filters) {
                                maxResults
                                businessReports {
                                    id
                                    name
                                    organization
                                    timezone
                                    dateRange {
                                        start
                                        end
                                        last
                                        lastFrame
                                        type
                                        from
                                    }
                                    campaigns {
                                        ids
                                        mode
                                    }
                                    campaignStatus
                                    creativeType
                                    iabCategory
                                    client
                                    dimensions
                                    attributes
                                    metrics
                                    engagements
                                    conversions
                                    ignore_filter_by_date_range
                                    showSpendOverdeliveries
                                    reportMode
                                    schemaVersion
                                    schedule {
                                        frequency
                                        date
                                        time
                                        day_of_week {
                                            mon
                                            tue
                                            wed
                                            thu
                                            fri
                                            sat
                                            sun
                                        }
                                        dayOfMonth
                                        recipients
                                    }
                                    scheduledReportTaskId
                                    createdBy
                                    updatedBy
                                    downloadUrl
                                    _deleted
                                    _etag
                                    _created
                                    _updated
                                    createdByFullName
                                }
                            }
                        }
                    `,
                        variables: {
                            filters,
                        },
                    })
                );

                dispatch({
                    type: 'SYSTEM__BUSINESS_REPORT__FETCH__SUCCESS',
                    payload: {
                        businessReports: businessReports.businessReports,
                    },
                });

                return {
                    businessReports: businessReports.businessReports,
                    maxResults: businessReports.maxResults,
                    allBusinessReportCount,
                    myBusinessReportCount,
                };
            } catch (err) {
                console.error(`GET ALL business reports error`, err);

                throw err;
            }
        };
    },
};

export default actions;
